import axios from 'axios';
import { useQuery } from 'react-query';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';

interface SetsResponse {
  data: any[];
  meta: {
    totalCount: number;
    totalPages: number;
  };
}

export interface ContentFilters {
  usedIn?: ('no_courses' | 'has_courses')[];
  goalType?: ('set' | 'assessment' | 'survey' | 'scorm')[];
  sharedStatus?: ('shared' | 'private')[];
}

const getSets = async (
  partnerId: string,
  includeCourseRelations: boolean = false,
  pageNumber: number = 1,
  pageSize: number = 25,
  sortParam: string = '-updated_at',
  searchName: string = '',
  filters: ContentFilters = {}
): Promise<SetsResponse> => {
  const endpoint = `/api/v3/partner/${partnerId}/sets`;

  let queryParams = `include=image,creator,courses&include_difficulty=true&sort=${sortParam}&page[number]=${pageNumber}&page[size]=${pageSize}`;
  if (includeCourseRelations) {
    queryParams += `&include_course_relations=true`;
  }
  if (searchName) {
    queryParams += `&name=${encodeURIComponent(searchName)}`;
  }

  // Add filter parameters
  if (filters.goalType && filters.goalType.length > 0) {
    filters.goalType.forEach((type) => {
      queryParams += `&filter[goal_type][]=${type}`;
    });
  }
  if (filters.usedIn && filters.usedIn.length > 0) {
    // Only apply filter if one option is selected (not both)
    if (filters.usedIn.includes('no_courses') && !filters.usedIn.includes('has_courses')) {
      queryParams += `&filter[has_courses]=false`;
    } else if (filters.usedIn.includes('has_courses') && !filters.usedIn.includes('no_courses')) {
      queryParams += `&filter[has_courses]=true`;
    }
    // If both are selected, no filter needed (show all)
  }
  if (filters.sharedStatus && filters.sharedStatus.length > 0) {
    // Only apply filter if one option is selected (not both)
    if (filters.sharedStatus.includes('shared') && !filters.sharedStatus.includes('private')) {
      queryParams += `&filter[shared]=true`;
    } else if (filters.sharedStatus.includes('private') && !filters.sharedStatus.includes('shared')) {
      queryParams += `&filter[shared]=false`;
    }
    // If both are selected, no filter needed (show all)
  }

  const response = await axios.get(`${endpoint}?${queryParams}`);
  const normalizedData = normalizeJSONAPIResponse(response.data);
  return {
    data: normalizedData,
    meta: response.data.meta,
  };
};

export const useGetSets = (
  partnerId: string,
  includeCourseRelations: boolean = false,
  pageNumber: number = 1,
  pageSize: number = 25,
  sortParam: string = '-updated_at',
  searchName: string = '',
  filters: ContentFilters = {}
) => {
  return useQuery(
    ['sets', partnerId, includeCourseRelations, pageNumber, pageSize, sortParam, searchName, filters],
    () => getSets(partnerId, includeCourseRelations, pageNumber, pageSize, sortParam, searchName, filters),
    {
      enabled: !!partnerId,
      keepPreviousData: true,
    }
  );
};
